// Cliente HTTP de los agentes IA del helpdesk (helpdesk/ai-agents).
//
// Calca el patrón de aiSettingsApi.js: mismo BFF, mismos headers Bearer, mismos
// errores tipados con `.status` para poder distinguir "backend aún no
// desplegado" (isEndpointMissing, re-exportado desde ahí — no se duplica) de un
// error real.
//
// SEGURIDAD: el token del proveedor que lleva un agente viaja SOLO en el body
// de POST/PUT (nunca en query/URL) y el backend jamás lo devuelve completo —
// el GET entrega únicamente has_token/token_last4. Prohibido loguear payloads
// acá.

import { isEndpointMissing } from "./aiSettingsApi";

// Typed error shared by every helpdesk client (status, code, capabilities and
// the backend validation lists in err.messages).
import { throwFetchApiError } from "../../../../utils/apiErrorMessage";
import { authFetch } from "../../../../services/authFetch";

// Re-export: las vistas de agentes importan todo desde este módulo, pero la
// implementación vive en un solo lugar (aiSettingsApi).
export { isEndpointMissing };

// REACT_APP_API_CONNECTION ya trae /api/v1/ al final; se recorta para armar
// rutas /api/v1/... sin duplicar el prefijo.
const BFF_BASE_URL = (process.env.REACT_APP_API_CONNECTION || "").replace(/\/api\/v1\/?$/, "");

const AGENTS_PATH = `${BFF_BASE_URL}/api/v1/helpdesk/ai-agents`;

function buildAuthHeaders(extra = {}) {
  // El Authorization lo pone authFetch (access vigente + un reintento ante 401).
  return {
    "x-app": "web",
    application: "web",
    ...extra,
  };
}

// Los ids son ObjectId pero llegan por URL: se codifican para que un valor
// inesperado no rompa el path ni inyecte segmentos extra.
const agentPath = (id, suffix = "") => `${AGENTS_PATH}/${encodeURIComponent(id)}${suffix}`;

/**
 * Lista de agentes IA del tenant. Contrato:
 * { ok, agents: [{ id, name, enabled, provider, model, system_prompt,
 *   has_token, token_last4, channels, created_at, updated_at }] } — nunca el
 * token completo.
 * @returns {Promise<object>}
 */
export async function getAiAgents() {
  const response = await authFetch(AGENTS_PATH, { headers: buildAuthHeaders() });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Catálogo de proveedores/modelos disponibles para configurar un agente.
 * Contrato: { ok, providers: [{ id, label, keyUrl, models: [{ id, label,
 * badge, pros, cons }] }] }. Ante 404/405 (isEndpointMissing) el caller usa la
 * copia local de aiModelCatalog.js.
 * @returns {Promise<object>}
 */
export async function getAiAgentsCatalog() {
  const response = await authFetch(`${AGENTS_PATH}/catalog`, { headers: buildAuthHeaders() });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Detalle de un agente. Contrato: { ok, agent: { ...mismo shape que la lista } }.
 * @param {string} id
 * @returns {Promise<object>}
 */
export async function getAiAgent(id) {
  const response = await authFetch(agentPath(id), { headers: buildAuthHeaders() });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Crea un agente. `payload.api_token` (token del proveedor) viaja SOLO en el
 * body; si se omite, el agente queda sin token propio.
 * @param {object} payload - { name, enabled, provider, model, system_prompt, api_token?, channels? }
 * @returns {Promise<object>} agente creado, sin el token completo
 */
export async function createAiAgent(payload) {
  const response = await authFetch(AGENTS_PATH, {
    method: "POST",
    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Actualiza un agente. Es parcial: un PUT sin `api_token` NO pisa el token ya
 * guardado — mandarlo solo si el usuario tipeó uno nuevo.
 * @param {string} id
 * @param {object} payload
 * @returns {Promise<object>} agente actualizado, sin el token completo
 */
export async function updateAiAgent(id, payload) {
  const response = await authFetch(agentPath(id), {
    method: "PUT",
    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Elimina un agente.
 * @param {string} id
 * @returns {Promise<{ok: boolean}>} un 204 sin body también resuelve
 */
export async function deleteAiAgent(id) {
  const response = await authFetch(agentPath(id), {
    method: "DELETE",
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  // Un DELETE puede responder 204 sin body: parsear JSON ahí tira SyntaxError.
  if (response.status === 204) return { ok: true };
  return response.json().catch(() => ({ ok: true }));
}

/**
 * Duplica un agente (copia de configuración para partir de una base ya
 * probada). El backend es quien decide qué se copia; el token del proveedor
 * NO se copia salvo que el contrato lo indique.
 * @param {string} id
 * @returns {Promise<object>} el nuevo agente
 */
export async function duplicateAiAgent(id) {
  const response = await authFetch(agentPath(id, "/duplicate"), {
    method: "POST",
    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Prueba de conexión real contra el proveedor del agente (user-triggered,
 * timeout en el backend). Manda api_token solo si el usuario tipeó uno; con {}
 * el backend usa el token guardado del agente.
 * @param {string} id
 * @param {object} [payload]
 * @returns {Promise<{ok: boolean, error?: string, reply?: string}>}
 */
export async function testAiAgent(id, payload = {}) {
  const response = await authFetch(agentPath(id, "/test"), {
    method: "POST",
    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}

/**
 * Métricas del agente en la ventana de días pedida.
 * Contrato: { ok, stats: { conversations, messages, transfers, avg_response_ms,
 * by_day: [...] } }.
 * @param {string} id
 * @param {number} [days=7] - Ventana en días (entero positivo)
 * @returns {Promise<object>}
 */
export async function getAiAgentStats(id, days = 7) {
  const qs = new URLSearchParams({ days: String(days) });
  const response = await authFetch(`${agentPath(id, "/stats")}?${qs}`, {
    headers: buildAuthHeaders(),
  });
  if (!response.ok) await throwFetchApiError(response);
  return response.json().catch(() => ({}));
}
